/**
 * Bulk static-tagging dialog for selected lists. Add or Remove mode; rows
 * without a catalog match are skipped and said so. Tags reappear on the rows
 * via the list-performance-tags invalidation the mutation hooks perform.
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FC } from 'react';
import { useMemo, useState } from 'react';
import { Tags, X } from 'lucide-react';

import { Button, LoadingSpinner } from '@/components/ui';
import { getApiErrorMessage } from '@/lib/utils';
import { useStaticTags } from '@/hooks/smswiser/static-tags/useStaticTags';
import {
  useBulkAssignStaticTags,
  useBulkUnassignStaticTags,
} from '@/hooks/smswiser/static-tags/useStaticTagAssignments';
import { useToast } from '@/providers/ToastProvider';
import { resolveWiserIds } from './resolveWiserIds';
import { TagPickList } from './TagPickList';

type Mode = 'add' | 'remove';

export interface TagListsDialogProps {
  countryKey: string;
  rows: Array<{ listId?: string | number; listName?: string }>;
  leadsLists: Array<{ id: number; name?: string; voluumId?: string | null }>;
  onClose: () => void;
  /** Called after a successful mutation — parent clears the selection. */
  onDone: () => void;
}

export const TagListsDialog: FC<TagListsDialogProps> = ({
  countryKey, rows, leadsLists, onClose, onDone,
}) => {
  const [mode, setMode] = useState<Mode>('add');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const tagsQuery = useStaticTags();
  const assign = useBulkAssignStaticTags(countryKey);
  const unassign = useBulkUnassignStaticTags(countryKey);
  const { success: toastSuccess, error: toastError } = useToast();

  const { wiserIds, unresolved } = useMemo(
    () => resolveWiserIds(rows, leadsLists),
    [rows, leadsLists],
  );

  const isBusy = assign.isPending || unassign.isPending;
  const canSubmit = selectedIds.length > 0 && wiserIds.length > 0 && !isBusy;

  const toggleTag = (tagId: string) =>
    setSelectedIds((prev) =>
      prev.includes(tagId) ? prev.filter((id) => id !== tagId) : [...prev, tagId],
    );

  const handleSubmit = async () => {
    const input = { staticTagIds: selectedIds, wiserIds };
    try {
      if (mode === 'add') {
        const out = await assign.mutateAsync(input);
        toastSuccess('Lists tagged', `${out.assigned} assignment(s) added.`);
      } else {
        const out = await unassign.mutateAsync(input);
        toastSuccess('Tags removed', `${out.removed} assignment(s) removed.`);
      }
      onDone();
    } catch (e) {
      toastError('Tagging failed', getApiErrorMessage(e, 'Could not update tags'));
    }
  };

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-background/95 p-4 backdrop-blur-sm"
      onClick={!isBusy ? onClose : undefined}
    >
      <div
        className="relative w-full max-w-md rounded-xl border border-border bg-card shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
      >
        <div className="flex shrink-0 items-center justify-between border-b border-border bg-muted/20 px-4 py-3">
          <h3 className="flex items-center gap-2 text-base font-semibold">
            <Tags className="h-4 w-4" />
            Tag lists
          </h3>
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onClose} disabled={isBusy} aria-label="Close">
            <X className="h-4 w-4" />
          </Button>
        </div>

        <div className="space-y-4 p-4">
          <p className="text-sm text-muted-foreground">
            {rows.length} list{rows.length !== 1 ? 's' : ''} selected.
          </p>
          {unresolved.length > 0 && (
            <p className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-2 text-xs text-amber-600 dark:text-amber-400">
              {unresolved.length} row{unresolved.length !== 1 ? 's have' : ' has'} no catalog match and will be skipped.
            </p>
          )}
          <div className="flex gap-1 rounded-lg bg-muted/40 p-1" role="radiogroup" aria-label="Mode">
            {(['add', 'remove'] as const).map((m) => (
              <button
                key={m}
                type="button"
                role="radio"
                aria-checked={mode === m}
                className={`flex-1 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                  mode === m ? 'bg-background shadow-sm' : 'text-muted-foreground hover:text-foreground'
                }`}
                onClick={() => setMode(m)}
              >
                {m === 'add' ? 'Add tags' : 'Remove tags'}
              </button>
            ))}
          </div>
          {tagsQuery.isLoading ? (
            <LoadingSpinner size="sm" />
          ) : (
            <TagPickList tags={tagsQuery.data ?? []} selectedIds={selectedIds} onToggle={toggleTag} />
          )}
        </div>

        <div className="flex shrink-0 justify-end gap-2 border-t border-border bg-muted/10 px-4 py-3">
          <Button variant="outline" onClick={onClose} disabled={isBusy}>
            Cancel
          </Button>
          <Button
            variant={mode === 'remove' ? 'destructive' : 'default'}
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="gap-2"
            aria-busy={isBusy}
          >
            {isBusy && <LoadingSpinner size="sm" />}
            {mode === 'add'
              ? `Add to ${wiserIds.length} list${wiserIds.length !== 1 ? 's' : ''}`
              : `Remove from ${wiserIds.length} list${wiserIds.length !== 1 ? 's' : ''}`}
          </Button>
        </div>
      </div>
    </div>
  );
};
